import api from '@/services/api';
import {
  LoginCredentials,
  SignupCredentials,
  LoginResponse,
  AuthError,
  ForgotPasswordRequest,
  ResetPasswordCredentials,
  VerifyTokenRequest,
  VerifyTokenResponse,
  ResendInvitationRequest,
} from '@/types/auth';
import { normalizeApiError } from '@/lib/errors';

export const authService = {
  getToken(): string | null {
    return localStorage.getItem('access_token');
  },

  setToken(token: string): void {
    localStorage.setItem('access_token', token);
  },

  removeToken(): void {
    localStorage.removeItem('access_token');
  },

  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  },

  setRefreshToken(token: string): void {
    localStorage.setItem('refresh_token', token);
  },

  removeRefreshToken(): void {
    localStorage.removeItem('refresh_token');
  },

  isAuthenticated(): boolean {
    return !!this.getToken();
  },

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>('/auth/signin', credentials);
      const { access_token, refresh_token } = response.data;

      this.setToken(access_token);
      if (refresh_token) {
        this.setRefreshToken(refresh_token);
      }

      return response.data;
    } catch (error: unknown) {
      throw normalizeApiError(error);
    }
  },

  async signup(credentials: SignupCredentials): Promise<LoginResponse> {
    try {
      const payload = {
        email: credentials.email,
        password: credentials.password,
        confirmationPassword: credentials.confirmationPassword
      };

      const headers: Record<string, string> = {};

      if (credentials.token) {
        headers['Authorization'] = `Bearer ${credentials.token}`;
      }

      const response = await api.post<LoginResponse>('/auth/signup', payload, { headers });
      const { access_token, refresh_token, role } = response.data;

      if (role !== 'PARTNER_VIEWER') {
        this.setToken(access_token);
        if (refresh_token) {
          this.setRefreshToken(refresh_token);
        }
      }

      return response.data;
    } catch (error: unknown) {
      throw normalizeApiError(error);
    }
  },

  async refreshToken(): Promise<LoginResponse> {
    const storedRefreshToken = this.getRefreshToken();
    if (!storedRefreshToken) {
      const authError: AuthError = normalizeApiError(new Error('Token de renovação não encontrado.'));
      throw authError;
    }
    try {
      const response = await api.post<LoginResponse>('/auth/refresh', { refresh_token: storedRefreshToken });
      const { access_token, refresh_token } = response.data;

      this.setToken(access_token);
      if (refresh_token) {
        this.setRefreshToken(refresh_token);
      }

      return response.data;
    } catch (error: unknown) {
      throw normalizeApiError(error);
    }
  },

  logout(): void {
    this.removeToken();
    this.removeRefreshToken();
  },

  async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    try {
      await api.post('/users/forgot-password', data);
    } catch (error: unknown) {
      throw normalizeApiError(error);
    }
  },

  async resetPassword(data: ResetPasswordCredentials): Promise<void> {
    try {
      await api.post('/users/confirm-reset-password', {
        token: data.token,
        newPassword: data.newPassword,
        passwordConfirmation: data.passwordConfirmation,
      });
    } catch (error: unknown) {
      throw normalizeApiError(error);
    }
  },

  async verifyToken(data: VerifyTokenRequest): Promise<VerifyTokenResponse> {
    try {
      const response = await api.post<VerifyTokenResponse>('/auth/verify-token', data);
      return response.data;
    } catch (error: unknown) {
      throw normalizeApiError(error);
    }
  },

  async resendInvitation(data: ResendInvitationRequest): Promise<void> {
    try {
      await api.post('/auth/resend-invitation', data);
    } catch (error: unknown) {
      throw normalizeApiError(error);
    }
  },
};